import React, { useState, useEffect } from "react";
import { passwordError, PASSWORD_HINT } from "@/lib/passwordPolicy";
import { useNavigate } from "react-router-dom";
import { KeyRound, User, Mail, Shield, CheckCircle2, AlertCircle, Smartphone, Copy, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const ROLE_LABEL = {
    super_admin: "Süper Yönetici",
    admin: "Yönetici",
    operations: "Operasyon",
    finance: "Finans",
    content: "İçerik Yöneticisi",
    customer: "Müşteri",
};

const INPUT = "w-full px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition";

export default function AdminProfilePage() {
    const { user, loading } = useAuth() || {};
    const navigate = useNavigate();
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    // 2FA state
    const [twoFA, setTwoFA] = useState({ enabled: false, backup_codes_remaining: 0 });
    const [setupData, setSetupData] = useState(null); // { secret, otpauth_url, label }
    const [enableCode, setEnableCode] = useState("");
    const [busy2FA, setBusy2FA] = useState(false);
    const [twoFAStatus, setTwoFAStatus] = useState(null);
    const [shownBackupCodes, setShownBackupCodes] = useState(null);
    const [disablePwd, setDisablePwd] = useState("");
    const [disableCode, setDisableCode] = useState("");

    // Load 2FA status on mount
    useEffect(() => {
        if (user) {
            api.get("/auth/2fa/status").then((r) => setTwoFA(r.data)).catch(() => {});
        }
    }, [user]);

    // Redirect to staff-login if no user (after auth loaded)
    useEffect(() => {
        if (!loading && !user) navigate("/staff-login");
    }, [loading, user, navigate]);

    if (loading) {
        return (
            <AdminLayout title="Profilim">
                <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>
            </AdminLayout>
        );
    }
    if (!user) return null;

    const submit = async (e) => {
        e.preventDefault();
        setStatus(null);
        if (passwordError(newPwd)) {
            setStatus({ type: "error", message: passwordError(newPwd) });
            return;
        }
        if (newPwd !== confirmPwd) {
            setStatus({ type: "error", message: "Yeni şifreler eşleşmiyor." });
            return;
        }
        if (currentPwd === newPwd) {
            setStatus({ type: "error", message: "Yeni şifre mevcut şifreyle aynı olamaz." });
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
            setStatus({ type: "success", message: "Şifreniz başarıyla güncellendi. Bir sonraki girişte yeni şifreyi kullanın." });
            setCurrentPwd("");
            setNewPwd("");
            setConfirmPwd("");
        } catch (err) {
            setStatus({ type: "error", message: err.response?.data?.detail || "İşlem başarısız." });
        } finally {
            setSaving(false);
        }
    };

    // 2FA handlers
    const start2FASetup = async () => {
        setTwoFAStatus(null);
        setBusy2FA(true);
        try {
            const { data } = await api.post("/auth/2fa/setup");
            setSetupData(data);
        } catch (err) {
            setTwoFAStatus({ type: "error", message: err.response?.data?.detail || "2FA kurulumu başarısız" });
        } finally {
            setBusy2FA(false);
        }
    };

    const enable2FA = async () => {
        setTwoFAStatus(null);
        if (!enableCode || enableCode.length < 6) {
            setTwoFAStatus({ type: "error", message: "6 haneli kod girin" });
            return;
        }
        setBusy2FA(true);
        try {
            const { data } = await api.post("/auth/2fa/enable", { code: enableCode });
            setShownBackupCodes(data.backup_codes);
            setSetupData(null);
            setEnableCode("");
            setTwoFA({ enabled: true, backup_codes_remaining: data.backup_codes.length });
            setTwoFAStatus({ type: "success", message: "✅ İki adımlı doğrulama etkinleştirildi! Aşağıdaki yedek kodları güvenli bir yere kaydedin." });
        } catch (err) {
            setTwoFAStatus({ type: "error", message: err.response?.data?.detail || "Doğrulama başarısız" });
        } finally {
            setBusy2FA(false);
        }
    };

    const disable2FA = async () => {
        if (!disablePwd) { setTwoFAStatus({ type: "error", message: "Şifrenizi girin" }); return; }
        if (!window.confirm("İki adımlı doğrulamayı devre dışı bırakmak istediğinize emin misiniz?")) return;
        setBusy2FA(true);
        setTwoFAStatus(null);
        try {
            await api.post("/auth/2fa/disable", { password: disablePwd, code: disableCode || undefined });
            setTwoFA({ enabled: false, backup_codes_remaining: 0 });
            setDisablePwd("");
            setDisableCode("");
            setShownBackupCodes(null);
            setTwoFAStatus({ type: "success", message: "İki adımlı doğrulama devre dışı bırakıldı." });
        } catch (err) {
            setTwoFAStatus({ type: "error", message: err.response?.data?.detail || "İşlem başarısız" });
        } finally {
            setBusy2FA(false);
        }
    };

    const regenerateBackupCodes = async () => {
        if (!window.confirm("Yeni yedek kodlar üretmek eski kodları geçersiz kılar. Devam edilsin mi?")) return;
        setBusy2FA(true);
        setTwoFAStatus(null);
        try {
            const { data } = await api.post("/auth/2fa/regenerate-backup-codes");
            setShownBackupCodes(data.backup_codes);
            setTwoFA((s) => ({ ...s, backup_codes_remaining: data.backup_codes.length }));
            setTwoFAStatus({ type: "success", message: "Yedek kodlar yenilendi. Eski kodlar artık geçersiz." });
        } catch (err) {
            setTwoFAStatus({ type: "error", message: err.response?.data?.detail || "İşlem başarısız" });
        } finally {
            setBusy2FA(false);
        }
    };

    const copyBackupCodes = () => {
        if (!shownBackupCodes) return;
        const text = shownBackupCodes.join("\n");
        navigator.clipboard.writeText(text);
        setTwoFAStatus({ type: "success", message: "Yedek kodlar panoya kopyalandı" });
    };

    return (
        <AdminLayout title="Profilim">
            <div className="max-w-3xl space-y-6" data-testid="admin-profile-page">
                {/* Account info card */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
                    <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-sky-500/20">
                            {(user.full_name || user.email || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white truncate">{user.full_name || "Personel"}</h2>
                            <p className="text-sm text-slate-500 truncate">{user.email}</p>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-start gap-2">
                            <User className="h-4 w-4 text-sky-500 mt-0.5" />
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ad Soyad</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user.full_name || "—"}</div>
                            </div>
                        </div>
                        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-start gap-2">
                            <Mail className="h-4 w-4 text-sky-500 mt-0.5" />
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">E-posta</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user.email}</div>
                            </div>
                        </div>
                        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-start gap-2">
                            <Shield className="h-4 w-4 text-sky-500 mt-0.5" />
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Yetki</div>
                                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{ROLE_LABEL[user.role] || user.role}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Password change card */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <KeyRound className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">Şifre Değiştir</h3>
                            <p className="text-xs text-slate-500">Güvenli bir şifre belirleyin — {PASSWORD_HINT}.</p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Mevcut Şifre</label>
                            <input
                                data-testid="profile-current-password"
                                type="password"
                                required
                                value={currentPwd}
                                onChange={(e) => setCurrentPwd(e.target.value)}
                                autoComplete="current-password"
                                className={INPUT}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Yeni Şifre</label>
                                <input
                                    data-testid="profile-new-password"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={newPwd}
                                    onChange={(e) => setNewPwd(e.target.value)}
                                    autoComplete="new-password"
                                    className={INPUT}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Yeni Şifre (Tekrar)</label>
                                <input
                                    data-testid="profile-confirm-password"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={confirmPwd}
                                    onChange={(e) => setConfirmPwd(e.target.value)}
                                    autoComplete="new-password"
                                    className={INPUT}
                                />
                            </div>
                        </div>

                        {status && (
                            <div data-testid="profile-status" className={`p-3 rounded-xl text-sm flex items-start gap-2 ${status.type === "success" ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-900" : "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900"}`}>
                                {status.type === "success" ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                                <span>{status.message}</span>
                            </div>
                        )}

                        <div className="flex justify-end pt-2">
                            <button
                                data-testid="profile-change-password-submit"
                                type="submit"
                                disabled={saving}
                                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-semibold text-sm shadow-lg shadow-sky-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                            >
                                {saving ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* 2FA card */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6" data-testid="profile-2fa-card">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                            <Smartphone className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">İki Adımlı Doğrulama (2FA)</h3>
                            <p className="text-xs text-slate-500">Google Authenticator, Microsoft Authenticator veya Authy ile ekstra güvenlik katmanı.</p>
                        </div>
                        <div>
                            {twoFA.enabled ? (
                                <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-bold">AKTİF</span>
                            ) : (
                                <span className="px-2.5 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold">KAPALI</span>
                            )}
                        </div>
                    </div>

                    {twoFAStatus && (
                        <div data-testid="profile-2fa-status" className={`mb-4 p-3 rounded-xl text-sm flex items-start gap-2 ${twoFAStatus.type === "success" ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-900" : "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900"}`}>
                            {twoFAStatus.type === "success" ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                            <span>{twoFAStatus.message}</span>
                        </div>
                    )}

                    {/* Backup codes display (shown once) */}
                    {shownBackupCodes && (
                        <div data-testid="profile-2fa-backup-codes" className="mb-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                            <div className="flex items-start gap-2 mb-3">
                                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <div className="font-bold text-sm text-amber-900 dark:text-amber-100">🔒 Yedek Kodlarınız</div>
                                    <div className="text-xs text-amber-800 dark:text-amber-200 mt-1">Telefonunuza erişemediğinizde bu kodlardan birini kullanarak giriş yapabilirsiniz. Her kod tek seferlik kullanılır. Bu listeyi güvenli bir yere kaydedin — bir daha gösterilmeyecek.</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 font-mono text-sm font-bold text-slate-900 dark:text-white">
                                {shownBackupCodes.map((c, i) => (
                                    <div key={i} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-center select-all">{c}</div>
                                ))}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <button onClick={copyBackupCodes} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-semibold transition-colors">
                                    <Copy className="h-3.5 w-3.5" /> Kopyala
                                </button>
                                <button onClick={() => setShownBackupCodes(null)} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors">
                                    Kaydettim, Kapat
                                </button>
                            </div>
                        </div>
                    )}

                    {/* If 2FA disabled and not in setup flow → Show "Enable" button */}
                    {!twoFA.enabled && !setupData && (
                        <button
                            data-testid="profile-2fa-enable-start"
                            onClick={start2FASetup}
                            disabled={busy2FA}
                            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-60 transition-all"
                        >
                            {busy2FA ? "Hazırlanıyor..." : "2FA Etkinleştir"}
                        </button>
                    )}

                    {/* Setup flow: show QR + code input */}
                    {setupData && (
                        <div data-testid="profile-2fa-setup" className="space-y-4">
                            <div className="text-sm text-slate-700 dark:text-slate-300">
                                <p className="font-semibold mb-2">1️⃣ Telefonunuza Authenticator uygulaması kurun (eğer yoksa)</p>
                                <ul className="text-xs text-slate-500 ml-4 list-disc space-y-0.5">
                                    <li>Google Authenticator (önerilen)</li>
                                    <li>Microsoft Authenticator</li>
                                    <li>Authy / 1Password / Bitwarden</li>
                                </ul>
                            </div>
                            <div className="text-sm text-slate-700 dark:text-slate-300">
                                <p className="font-semibold mb-2">2️⃣ QR kodu tarayın</p>
                                <div className="flex flex-col sm:flex-row items-start gap-4">
                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                        <QRCodeSVG value={setupData.otpauth_url} size={180} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-slate-500 mb-1">veya bu kodu manuel girin:</p>
                                        <code className="block text-xs font-mono bg-slate-100 dark:bg-slate-800 p-2 rounded-lg break-all">{setupData.secret}</code>
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm text-slate-700 dark:text-slate-300">
                                <p className="font-semibold mb-2">3️⃣ Uygulamanın gösterdiği 6 haneli kodu girin</p>
                                <input
                                    data-testid="profile-2fa-enable-code"
                                    type="text"
                                    value={enableCode}
                                    onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="123456"
                                    className="w-full sm:w-48 px-3 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-2xl font-mono font-bold text-center tracking-[0.4em] text-slate-900 dark:text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid="profile-2fa-enable-confirm"
                                    onClick={enable2FA}
                                    disabled={busy2FA || enableCode.length !== 6}
                                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                                >
                                    {busy2FA ? "Doğrulanıyor..." : "Etkinleştir"}
                                </button>
                                <button onClick={() => { setSetupData(null); setEnableCode(""); }} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold">İptal</button>
                            </div>
                        </div>
                    )}

                    {/* If 2FA enabled → show backup count + disable form */}
                    {twoFA.enabled && (
                        <div data-testid="profile-2fa-enabled" className="space-y-4">
                            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Yedek Kodlar</div>
                                    <div className="text-sm font-semibold text-slate-900 dark:text-white">{twoFA.backup_codes_remaining} adet kullanılabilir</div>
                                </div>
                                <button
                                    data-testid="profile-2fa-regen-backup"
                                    onClick={regenerateBackupCodes}
                                    disabled={busy2FA}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                >
                                    <RefreshCw className="h-3.5 w-3.5" /> Yenile
                                </button>
                            </div>
                            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">2FA'yı Devre Dışı Bırak</div>
                                <p className="text-xs text-slate-500 mb-3">Devre dışı bırakmadan önce hesap şifrenizi doğrulamamız gerekiyor.</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <input
                                        data-testid="profile-2fa-disable-password"
                                        type="password"
                                        value={disablePwd}
                                        onChange={(e) => setDisablePwd(e.target.value)}
                                        placeholder="Hesap şifreniz"
                                        className={INPUT}
                                    />
                                    <input
                                        data-testid="profile-2fa-disable-code"
                                        type="text"
                                        value={disableCode}
                                        onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="6 haneli kod (opsiyonel)"
                                        className={INPUT}
                                    />
                                </div>
                                <button
                                    data-testid="profile-2fa-disable-submit"
                                    onClick={disable2FA}
                                    disabled={busy2FA || !disablePwd}
                                    className="mt-3 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
                                >
                                    {busy2FA ? "İşleniyor..." : "Devre Dışı Bırak"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
